import { useState, useCallback, useRef } from "react";
import { MODES, OBJECT_TYPES, GRID_CELL_SIZE_M } from "../configuratorConstants";

const useMouseInteractions = ({
  viewTransform,
  modifierKeys,
  mainContainerRef,
  svgRef,
  setViewTransform,
  activeMode,
  setSelectedObjectId,
  screenToWorld,
  updateModulePosition,
  snapAndFinalizeModulePosition,
  onWallSegmentClick,
  elementTypeToPlace,
  dispatch,
}) => {
  const [draggingState, setDraggingState] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const mouseDownStartPosRef = useRef(null);

  const handleMouseDownOnCanvas = useCallback(
    (e) => {
      mainContainerRef.current?.focus();
      if (e.button !== 0 && e.button !== 1) return;

      // Пропускаем обработку для безкаркасного режима рисования стен
      if (activeMode === MODES.FRAMELESS && elementTypeToPlace === OBJECT_TYPES.FRAMELESS_WALL_TOOL) {
        return;
      }

      // --- НАЧАЛО ИЗМЕНЕНИЯ: Обновляем селектор для всех типов стен ---
      if (elementTypeToPlace && e.button === 0) {
        // Специальная обработка для жалюзи - кликаем по окнам
        if (elementTypeToPlace === OBJECT_TYPES.WINDOW_BLINDS) {
          // Ищем окно вверх по дереву DOM
          let clickedWindowElement = e.target.closest('[data-object-type="window"], [data-object-type="panoramic_window"]');

          // Если не нашли через closest, проверяем сам target
          if (!clickedWindowElement && (e.target.getAttribute("data-object-type") === "window" || e.target.getAttribute("data-object-type") === "panoramic_window")) {
            clickedWindowElement = e.target;
          }

          // Также проверяем родительские элементы
          if (!clickedWindowElement) {
            let parent = e.target.parentElement;
            let depth = 0;
            while (parent && depth < 5) {
              const objType = parent.getAttribute("data-object-type");
              if (objType === "window" || objType === "panoramic_window") {
                clickedWindowElement = parent;
                break;
              }
              parent = parent.parentElement;
              depth++;
            }
          }

          if (clickedWindowElement) {
            if (process.env.NODE_ENV !== "production") {
              console.log('[useMouseInteractions] Window clicked for blinds placement', {
                target: e.target,
                targetType: e.target.getAttribute("data-object-type"),
                clickedWindowElement,
                elementType: clickedWindowElement.getAttribute("data-object-type"),
                allAttributes: {
                  'data-object-id': clickedWindowElement.getAttribute("data-object-id"),
                  'data-object-type': clickedWindowElement.getAttribute("data-object-type"),
                  'data-segment-id': clickedWindowElement.getAttribute("data-segment-id"),
                  'data-parent-wall-id': clickedWindowElement.getAttribute("data-parent-wall-id")
                }
              });
            }

            const windowId = clickedWindowElement.getAttribute("data-object-id");
            const windowType = clickedWindowElement.getAttribute("data-object-type");

            if (!windowId) {
              if (process.env.NODE_ENV !== "production") {
                console.warn('[useMouseInteractions] Window element has no data-object-id');
              }
              return;
            }

            // Находим родительский сегмент стены (для модульного и безкаркасного режима)
            // Используем closest, как в модульном режиме
            const wallSegmentElement = clickedWindowElement.closest('[data-segment-id]');
            if (wallSegmentElement) {
              const segmentId = wallSegmentElement.getAttribute("data-segment-id");
              const moduleId = wallSegmentElement.getAttribute("data-module-id");
              const parentWallId = wallSegmentElement.getAttribute("data-parent-wall-id");

              if (process.env.NODE_ENV !== "production") {
                console.log('[useMouseInteractions] Found segment via closest', {
                  segmentId,
                  moduleId,
                  parentWallId,
                  windowId,
                  windowType
                });
              }

              // Получаем позицию окна на сегменте из самого элемента окна
              // Нужно найти элемент в данных модуля, чтобы получить его positionOnSegment
              const worldCoords = screenToWorld(e.clientX, e.clientY);

              // Вызываем onWallSegmentClick с информацией о том, что это клик по окну для жалюзи
              if (onWallSegmentClick) {
                e.stopPropagation();
                e.preventDefault();
                onWallSegmentClick(segmentId, {
                  segmentData: { id: segmentId, parentWallId: parentWallId },
                  windowElement: { id: windowId, type: windowType },
                  isBlindsPlacement: true,
                  worldCoords: worldCoords
                });
              }
              return;
            }

            // Обработка для безкаркасного режима
            // Сначала проверяем, есть ли атрибуты сегмента прямо на элементе окна
            let segmentId = clickedWindowElement.getAttribute("data-segment-id");
            let parentWallId = clickedWindowElement.getAttribute("data-parent-wall-id");

            // Если атрибуты не на элементе, ищем их в родительских элементах
            if (!segmentId) {
              let parent = clickedWindowElement.parentElement;
              let depth = 0;
              while (parent && depth < 10) {
                const segId = parent.getAttribute("data-segment-id");
                const wallId = parent.getAttribute("data-parent-wall-id");
                if (segId) {
                  segmentId = segId;
                  if (wallId) parentWallId = wallId;
                  break;
                }
                parent = parent.parentElement;
                depth++;
              }
            }

            if (segmentId) {
              // Атрибуты есть прямо на элементе окна
              const worldCoords = screenToWorld(e.clientX, e.clientY);

              if (process.env.NODE_ENV !== "production") {
                console.log('[useMouseInteractions] Found segment from window attributes', {
                  segmentId,
                  parentWallId,
                  windowId,
                  windowType
                });
              }

              // Вызываем onWallSegmentClick с информацией о том, что это клик по окну для жалюзи
              if (onWallSegmentClick) {
                e.stopPropagation();
                e.preventDefault();
                onWallSegmentClick(segmentId, {
                  segmentData: { id: segmentId, parentWallId: parentWallId },
                  windowElement: { id: windowId, type: windowType },
                  isBlindsPlacement: true,
                  worldCoords: worldCoords
                });
              } else {
                if (process.env.NODE_ENV !== "production") {
                  console.warn('[useMouseInteractions] onWallSegmentClick is not defined');
                }
              }
              return;
            }

            // Если атрибутов нет на элементе, ищем родительский сегмент
            // Пробуем разные способы поиска
            let framelessWallSegmentElement = clickedWindowElement.closest('[data-object-type="frameless_wall_segment"]');

            // Если не нашли через closest, ищем вручную вверх по дереву
            if (!framelessWallSegmentElement) {
              let parent = clickedWindowElement.parentElement;
              let depth = 0;
              while (parent && depth < 15) {
                const objType = parent.getAttribute("data-object-type");
                if (objType === "frameless_wall_segment") {
                  framelessWallSegmentElement = parent;
                  break;
                }
                parent = parent.parentElement;
                depth++;
              }
            }

            if (framelessWallSegmentElement) {
              const segmentIdFromParent = framelessWallSegmentElement.getAttribute("data-object-id");
              const parentWallIdFromParent = framelessWallSegmentElement.getAttribute("data-parent-wall-id");

              if (process.env.NODE_ENV !== "production") {
                console.log('[useMouseInteractions] Found segment from parent element', {
                  segmentId: segmentIdFromParent,
                  parentWallId: parentWallIdFromParent,
                  windowId,
                  windowType,
                  framelessWallSegmentElement
                });
              }

              if (!segmentIdFromParent) {
                if (process.env.NODE_ENV !== "production") {
                  console.warn('[useMouseInteractions] Segment element has no data-object-id', framelessWallSegmentElement);
                }
                return;
              }

              const worldCoords = screenToWorld(e.clientX, e.clientY);

              // Вызываем onWallSegmentClick с информацией о том, что это клик по окну для жалюзи
              if (onWallSegmentClick) {
                e.stopPropagation();
                e.preventDefault();
                onWallSegmentClick(segmentIdFromParent, {
                  segmentData: { id: segmentIdFromParent, parentWallId: parentWallIdFromParent },
                  windowElement: { id: windowId, type: windowType },
                  isBlindsPlacement: true,
                  worldCoords: worldCoords
                });
              } else {
                if (process.env.NODE_ENV !== "production") {
                  console.warn('[useMouseInteractions] onWallSegmentClick is not defined');
                }
              }
              return;
            }

            // Последняя попытка: ищем сегмент вручную, проходя по всем родительским элементам
            let foundSegmentId = null;
            let foundParentWallId = null;
            let parent = clickedWindowElement;
            let depth = 0;

            while (parent && depth < 20) {
              const segId = parent.getAttribute("data-segment-id");
              const wallId = parent.getAttribute("data-parent-wall-id");
              const objType = parent.getAttribute("data-object-type");
              const objId = parent.getAttribute("data-object-id");

              // Если нашли сегмент напрямую
              if (segId) {
                foundSegmentId = segId;
                if (wallId) foundParentWallId = wallId;
                break;
              }

              // Если это сегмент стены, используем его ID
              if (objType === "frameless_wall_segment" && objId) {
                foundSegmentId = objId;
                foundParentWallId = wallId || parent.getAttribute("data-parent-wall-id");
                break;
              }

              parent = parent.parentElement;
              depth++;
            }

            if (foundSegmentId) {
              if (process.env.NODE_ENV !== "production") {
                console.log('[useMouseInteractions] Found segment by manual search', {
                  segmentId: foundSegmentId,
                  parentWallId: foundParentWallId,
                  windowId,
                  windowType,
                  depth
                });
              }

              const worldCoords = screenToWorld(e.clientX, e.clientY);

              if (onWallSegmentClick) {
                e.stopPropagation();
                e.preventDefault();
                onWallSegmentClick(foundSegmentId, {
                  segmentData: { id: foundSegmentId, parentWallId: foundParentWallId },
                  windowElement: { id: windowId, type: windowType },
                  isBlindsPlacement: true,
                  worldCoords: worldCoords
                });
              }
              return;
            }

            if (process.env.NODE_ENV !== "production") {
              console.warn('[useMouseInteractions] Could not find segment for window', {
                windowId,
                windowType,
                clickedWindowElement,
                windowElementParent: clickedWindowElement.parentElement,
                windowElementParentType: clickedWindowElement.parentElement?.getAttribute("data-object-type"),
                allParents: (() => {
                  const parents = [];
                  let p = clickedWindowElement;
                  let d = 0;
                  while (p && d < 20) {
                    parents.push({
                      tag: p.tagName,
                      'data-object-type': p.getAttribute("data-object-type"),
                      'data-object-id': p.getAttribute("data-object-id"),
                      'data-segment-id': p.getAttribute("data-segment-id"),
                      'data-parent-wall-id': p.getAttribute("data-parent-wall-id")
                    });
                    p = p.parentElement;
                    d++;
                  }
                  return parents;
                })()
              });
            }
          }
        }

        const clickedPlaceableElement = e.target.closest('[data-object-type="wall_segment"], [data-object-type="docking_joint"], [data-object-type="frameless_wall_segment"]');
        if (clickedPlaceableElement) {
          // Если размещается ролета, не обрабатываем клик на сегменте стены
          // Ролета должна размещаться только при клике на окно
          if (elementTypeToPlace === OBJECT_TYPES.WINDOW_BLINDS) {
            return;
          }

          const objectId = clickedPlaceableElement.getAttribute("data-object-id");
          const objectType = clickedPlaceableElement.getAttribute("data-object-type");
          const worldCoords = screenToWorld(e.clientX, e.clientY);
          let positionOnSegment = 0.5;
          let clickSide = null;

          try {
            if (objectType === 'docking_joint') {
              const jointX = parseFloat(clickedPlaceableElement.getAttribute("data-joint-x"));
              const jointY = parseFloat(clickedPlaceableElement.getAttribute("data-joint-y"));
              const jointRot = parseFloat(clickedPlaceableElement.getAttribute("data-joint-rotation"));
              const jointWidthM = parseFloat(clickedPlaceableElement.getAttribute("data-joint-width"));
              const sandwichWidthM = parseFloat(clickedPlaceableElement.getAttribute("data-sandwich-width"));
              const finalOffsetM = parseFloat(clickedPlaceableElement.getAttribute("data-final-offset"));

              // Определяем сторону клика для двойных стыков
              const delta = { x: worldCoords.x - jointX, y: worldCoords.y - jointY };
              const angleRad = -jointRot * Math.PI / 180;
              const cos = Math.cos(angleRad);
              const sin = Math.sin(angleRad);
              const localClickY = delta.x * sin + delta.y * cos;

              // Если клик выше центра стыка - сторона 1 (positiveSide), ниже - сторона -1 (negativeSide)
              clickSide = localClickY > 0 ? 1 : -1;

              console.log('Click side detection:', {
                localClickY, clickSide, jointRot, delta, worldCoords, jointX, jointY,
                sideDescription: clickSide === 1 ? 'positiveSide (above center)' : 'negativeSide (below center)'
              });

              // Получаем информацию о сегменте, если доступна
              const segmentIndex = clickedPlaceableElement.getAttribute("data-segment-index");
              const segmentWidthM = clickedPlaceableElement.getAttribute("data-segment-width");

              if (segmentIndex !== null && segmentWidthM !== null) {
                // Новый подход: используем информацию о конкретном сегменте
                const segmentIdx = parseInt(segmentIndex, 10);
                const segmentWidth = parseFloat(segmentWidthM);
                // ВАЖНО: используем реальную ширину стыка для правильного расчета количества слотов
                const numSlots = Math.max(1, Math.round(jointWidthM / GRID_CELL_SIZE_M));

                // ИСПРАВЛЕНИЕ: используем точную позицию в центре сегмента с небольшим смещением
                // чтобы избежать проблем с округлением на границах сегментов
                // Добавляем маленькое смещение (0.5001 вместо 0.5) чтобы floor() всегда давал правильный индекс
                positionOnSegment = (segmentIdx + 0.5001) / numSlots;

                console.log(`🎯 КЛИК НА СЕГМЕНТ СТЫКА #${segmentIdx} (из ${numSlots} сегментов)`, {
                  'Номер сегмента (от 0)': segmentIdx,
                  'Номер сегмента (от 1)': segmentIdx + 1,
                  'Всего сегментов': numSlots,
                  'Ширина стыка (м)': jointWidthM,
                  'Позиция на стыке': positionOnSegment.toFixed(3),
                  'Сторона': clickSide === 1 ? 'ПРАВАЯ' : 'ЛЕВАЯ'
                });

                console.log('✅ Segment-based positioning:', {
                  segmentIdx,
                  jointWidthM,
                  numSlots,
                  positionOnSegment,
                  targetSlot: Math.floor(positionOnSegment * numSlots),
                  verification: `Should be ${segmentIdx}, calculated ${Math.floor(positionOnSegment * numSlots)}`
                });
              } else {
                // Старый подход: рассчитываем позицию на основе клика
                const localClickX_from_center = delta.x * cos - delta.y * sin;

                // Отладочная информация
                console.log('Joint positioning debug:', {
                  jointX, jointY, jointRot, jointWidthM, sandwichWidthM, finalOffsetM,
                  worldCoords, delta, localClickX_from_center
                });

                // Используем ту же логику, что и в renderElements
                // availableWidth = jointWidthPx - 2 * finalOffset
                // positionX = (positionOnSegment || 0.5) * availableWidth - availableWidth / 2

                // Обратный расчет: из positionX получаем positionOnSegment
                const availableWidthM = sandwichWidthM;
                const scale = viewTransform.scale;
                const availableWidthPx = availableWidthM * scale;
                const finalOffsetPx = finalOffsetM * scale;

                // Позиция клика относительно центра стыка в пикселях
                const clickPosPx = localClickX_from_center;

                // Логика рендеринга: positionX = (positionOnSegment || 0.5) * availableWidth - availableWidth / 2
                // Обратный расчет: из positionX получаем positionOnSegment
                // positionOnSegment = (positionX + availableWidth / 2) / availableWidth

                // Позиция клика относительно центра стыка в пикселях
                const positionX = clickPosPx;

                // Рассчитываем positionOnSegment по формуле рендеринга
                if (availableWidthPx > 0) {
                  positionOnSegment = (positionX + availableWidthPx / 2) / availableWidthPx;
                  positionOnSegment = Math.max(0, Math.min(1, positionOnSegment));
                  console.log('Calculated position:', {
                    clickPosPx, availableWidthPx, positionX, positionOnSegment
                  });
                } else {
                  positionOnSegment = 0.5;
                }
              }
            } else if (objectType === 'frameless_wall_segment' || objectType === 'wall_segment') {
              // Для простых стен элемент всегда ставится по центру, но мы передадим worldCoords для точного расчета
              positionOnSegment = 0.5;
            }
            positionOnSegment = Math.max(0, Math.min(1, positionOnSegment));
          } catch (err) { console.error("Could not calculate position on segment:", err); }

          if (objectId && onWallSegmentClick) {
            e.stopPropagation();

            onWallSegmentClick(objectId, {
              altKey: modifierKeys.alt,
              positionOnSegment,
              clickSide,
              worldCoords // --- ДОБАВЛЕНО: Передаем мировые координаты ---
            });
            return;
          }
        }
      }
      // --- КОНЕЦ ИЗМЕНЕНИЯ ---

      if (e.button === 1) {
        setIsPanning(true);
        setDraggingState({
          isPanning: true,
          startScreenX: e.clientX,
          startScreenY: e.clientY,
          initialViewX: viewTransform.x,
          initialViewY: viewTransform.y,
        });
        mouseDownStartPosRef.current = { x: e.clientX, y: e.clientY };
        e.preventDefault();
        return;
      }

      const isClickOnEmpty = e.target === svgRef.current || e.target.id === "grid" || e.target.closest("g#grid");
      const clickedObjectElement = e.target.closest('[data-object-id]');

      if (e.button === 0 && (activeMode === MODES.FRAME || activeMode === MODES.FRAMELESS)) {
        if (isClickOnEmpty && !elementTypeToPlace) {
          setSelectedObjectId(null);
          setIsPanning(true);
          setDraggingState({
            isPanning: true,
            startScreenX: e.clientX,
            startScreenY: e.clientY,
            initialViewX: viewTransform.x,
            initialViewY: viewTransform.y,
          });
          mouseDownStartPosRef.current = { x: e.clientX, y: e.clientY };
          e.preventDefault();
          return;
        } else if (clickedObjectElement) {
          const objectId = clickedObjectElement.getAttribute('data-object-id');
          if (objectId && setSelectedObjectId) {
            setSelectedObjectId(objectId);
            e.stopPropagation();
            return;
          }
        }
      }

      if (e.button === 0 && activeMode === MODES.MODULAR && !elementTypeToPlace) {
        const clickedModuleElement = e.target.closest('[data-object-type="module"]');
        if (clickedModuleElement) {
          const moduleId = clickedModuleElement.getAttribute("data-object-id");
          const moduleInitialX = parseFloat(clickedModuleElement.getAttribute("data-module-x") || "0");
          const moduleInitialY = parseFloat(clickedModuleElement.getAttribute("data-module-y") || "0");
          const moduleInitialRotation = parseFloat(clickedModuleElement.getAttribute("data-module-rotation") || "0");
          const worldCoords = screenToWorld(e.clientX, e.clientY);
          if (moduleId) {
            setSelectedObjectId(moduleId);
            setDraggingState({
              isDraggingModule: true,
              moduleId: moduleId,
              dragStartWorldX: worldCoords.x,
              dragStartWorldY: worldCoords.y,
              initialModuleX: moduleInitialX,
              initialModuleY: moduleInitialY,
              initialModuleRotation: moduleInitialRotation,
            });
            e.stopPropagation();
            return;
          }
        }

        if (clickedObjectElement && !clickedModuleElement) {
          const objectId = clickedObjectElement.getAttribute('data-object-id');
          const objectType = clickedObjectElement.getAttribute('data-object-type');

          // Если кликнули на стык для выделения
          if (objectType === 'docking_joint') {
            const segmentIndex = clickedObjectElement.getAttribute("data-segment-index");
            if (segmentIndex !== null) {
              const segmentIdx = parseInt(segmentIndex, 10);
              console.log(`🎯 ВЫБРАН СТЫК - кликнули на сегмент #${segmentIdx} (от 0) = сегмент #${segmentIdx + 1} (от 1)`);

              // Сохраняем номер сегмента в state
              dispatch?.({
                type: 'SET_CLICKED_JOINT_SEGMENT',
                payload: segmentIdx
              });
            } else {
              console.log(`🎯 ВЫБРАН СТЫК (клик не на сегмент)`);
              dispatch?.({
                type: 'SET_CLICKED_JOINT_SEGMENT',
                payload: null
              });
            }
          }

          if (objectId && setSelectedObjectId) {
            setSelectedObjectId(objectId);
            e.stopPropagation();
            return;
          }
        }

        if (isClickOnEmpty) {
          setSelectedObjectId(null);
          setIsPanning(true);
          setDraggingState({
            isPanning: true,
            startScreenX: e.clientX,
            startScreenY: e.clientY,
            initialViewX: viewTransform.x,
            initialViewY: viewTransform.y,
          });
          mouseDownStartPosRef.current = { x: e.clientX, y: e.clientY };
          e.preventDefault();
          return;
        }
      }
    },
    [
      modifierKeys, viewTransform.x, viewTransform.y, mainContainerRef,
      activeMode, setSelectedObjectId, screenToWorld, svgRef,
      elementTypeToPlace, onWallSegmentClick, dispatch,
    ],
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (draggingState?.isPanning) {
        if (!mouseDownStartPosRef.current) return;
        const dxScreen = e.clientX - mouseDownStartPosRef.current.x;
        const dyScreen = e.clientY - mouseDownStartPosRef.current.y;
        setViewTransform((prev) => ({
          ...prev,
          x: draggingState.initialViewX + dxScreen,
          y: draggingState.initialViewY + dyScreen,
        }));
      } else if (draggingState?.isDraggingModule) {
        /*
        const {
          moduleId, dragStartWorldX, dragStartWorldY, initialModuleX, initialModuleY
        } = draggingState;
        const currentMouseWorld = screenToWorld(e.clientX, e.clientY);
        const deltaWorldX = currentMouseWorld.x - dragStartWorldX;
        const deltaWorldY = currentMouseWorld.y - dragStartWorldY;
        const newModuleX = initialModuleX + deltaWorldX;
        const newModuleY = initialModuleY + deltaWorldY;
        updateModulePosition(moduleId, newModuleX, newModuleY);
        */
      }
    },
    [draggingState, setViewTransform, screenToWorld, updateModulePosition],
  );

  const handleMouseUp = useCallback(
    (_e) => {
      if (isPanning) setIsPanning(false);
      if (draggingState?.isDraggingModule) {
        snapAndFinalizeModulePosition(
          draggingState.moduleId,
          draggingState.initialModuleX,
          draggingState.initialModuleY,
          draggingState.initialModuleRotation
        );
      }
      setDraggingState(null);
      mouseDownStartPosRef.current = null;
    },
    [isPanning, draggingState, snapAndFinalizeModulePosition],
  );

  const handleMouseLeave = useCallback(() => {
    if (isPanning || draggingState?.isDraggingModule) {
      if (draggingState?.isDraggingModule) {
        snapAndFinalizeModulePosition(
          draggingState.moduleId,
          draggingState.initialModuleX,
          draggingState.initialModuleY,
          draggingState.initialModuleRotation
        );
      }
      setIsPanning(false);
      setDraggingState(null);
      mouseDownStartPosRef.current = null;
    }
  }, [isPanning, draggingState, snapAndFinalizeModulePosition]);

  return {
    isPanning,
    isDraggingModule: !!draggingState?.isDraggingModule,
    handleMouseDownOnCanvas,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
  };
};

export default useMouseInteractions;